import { Button, IconPlus } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'
import { useIntl } from 'react-intl'
import type { Restrictions as RestrictionsType } from 'typings'
import { useFormStateContext } from '../../../../providers'
import {
  CONNECTOR,
  Rule,
  RuleActions,
  RuleDefaultActions,
  RuleStack,
} from '../../RuleStack'
import { useRestrictionsContext } from '../hooks/useRestrictions.constate'
import { messages } from '../messages'
import { RESTRICTIONS, RestrictionsMap } from '../typings'
import { MaxItemsPerCartView } from './MaxItemPerCartView'
import { MaxItemsPerAttributeView } from './MaxItemsPerAttributeView'
import { MaxUsagePerClientView } from './MaxUsagePerClientView'
import { MaxUsageView } from './MaxUsageView'
import { useMemo } from 'react'

type Props = {
  onEdit: () => void
  onRemove: (name: string) => void
}

const viewMap: Record<
  RestrictionsMap,
  ({
    restriction,
  }: {
    restriction: RestrictionsType[RestrictionsMap]
  }) => JSX.Element
> = {
  [RestrictionsMap.MAX_ITEMS_PER_CART]: MaxItemsPerCartView,
  [RestrictionsMap.MAX_USAGE]: MaxUsageView,
  [RestrictionsMap.MAX_USAGE_PER_CLIENT]: MaxUsagePerClientView,
  [RestrictionsMap.MAX_ITEMS_PER_ATTRIBUTE]: MaxItemsPerAttributeView,
}

const ViewComponent = ({
  restrictionKey,
  restriction,
}: {
  restrictionKey: RestrictionsMap
  restriction: RestrictionsType[RestrictionsMap]
}) => {
  const Component = viewMap[restrictionKey]

  if (Component) return <Component restriction={restriction} />

  return null
}

export function View(props: Props) {
  const { onEdit, onRemove } = props

  const { readOnly, form } = useFormStateContext()

  const { visibleRestrictions } = useRestrictionsContext()

  const { $t } = useIntl()

  const restrictions: RestrictionsType = useWatch({ form, name: RESTRICTIONS })

  const allRestrictionsFilled: boolean = useMemo(
    () =>
      visibleRestrictions.every(
        (restriction) => restrictions[restriction.props.name]
      ),
    [restrictions, visibleRestrictions]
  )

  const hideAddButton: boolean = allRestrictionsFilled || readOnly

  return (
    <>
      <RuleStack>
        {visibleRestrictions.map((restriction, index) => {
          const key = restriction.props.name
          const restrictionValue = restrictions[key]
          if (restrictionValue === undefined) return null

          return (
            <Rule onClick={onEdit} connector={CONNECTOR.AND} key={index}>
              <ViewComponent
                restrictionKey={key as RestrictionsMap}
                restriction={restrictionValue}
              />
              <RuleActions readonly={readOnly}>
                <RuleDefaultActions
                  onEdit={onEdit}
                  onRemove={() => onRemove(key)}
                />
              </RuleActions>
            </Rule>
          )
        })}
      </RuleStack>
      <Button
        hidden={hideAddButton}
        variant="secondary"
        onClick={onEdit}
        icon={<IconPlus />}
      >
        {$t(messages.add)}
      </Button>
    </>
  )
}
